import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  Alert,
  CircularProgress,
  Fab,
} from "@mui/material";
import {
  Add as AddIcon,
  Traffic as TrafficIcon,
  Construction as ConstructionIcon,
  CarCrash as AccidentIcon,
  Warning as WarningIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import MapView from "./MapView";
import { config } from "../config";

function Incidents() {
  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newIncident, setNewIncident] = useState({
    type: "traffic_jam",
    description: "",
    severity: "medium",
    lat: "",
    lng: "",
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadIncidents();
  }, []);

  const loadIncidents = async () => {
    setLoading(true);
    setError("");

    try {
      const response = await fetch(config.getApiUrl("/incidents"));
      const data = await response.json();

      if (data.success) {
        setIncidents(data.incidents);
      } else {
        setError(data.error || "Fehler beim Laden der Vorfälle");
      }
    } catch (error) {
      console.error("Incidents load error:", error);
      setError("Verbindungsfehler beim Laden der Vorfälle");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitIncident = async () => {
    if (!newIncident.description || !newIncident.lat || !newIncident.lng) {
      setError("Bitte füllen Sie alle Felder aus");
      return;
    }

    setSubmitting(true);
    setError("");

    try {
      const response = await fetch(config.getApiUrl("/incidents"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(newIncident),
      });

      const data = await response.json();

      if (data.success) {
        setDialogOpen(false);
        setNewIncident({
          type: "traffic_jam",
          description: "",
          severity: "medium",
          lat: "",
          lng: "",
        });
        loadIncidents(); // Neuladen der Liste
      } else {
        setError(data.error || "Fehler beim Melden des Vorfalls");
      }
    } catch (error) {
      console.error("Incident submit error:", error);
      setError("Verbindungsfehler beim Melden des Vorfalls");
    } finally {
      setSubmitting(false);
    }
  };

  const handleMapClick = (e) => {
    if (dialogOpen) {
      setNewIncident((prev) => ({
        ...prev,
        lat: e.latlng.lat.toFixed(6),
        lng: e.latlng.lng.toFixed(6),
      }));
    }
  };

  const getIncidentIcon = (type) => {
    switch (type) {
      case "traffic_jam":
        return <TrafficIcon color="error" />;
      case "accident":
        return <AccidentIcon color="error" />;
      case "construction":
        return <ConstructionIcon color="warning" />;
      case "road_closed":
        return <CloseIcon color="error" />;
      default:
        return <WarningIcon color="action" />;
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case "low":
        return "success";
      case "medium":
        return "warning";
      case "high":
        return "error";
      case "critical":
        return "error";
      default:
        return "default";
    }
  };

  const getIncidentTitle = (type) => {
    const titles = {
      traffic_jam: "Verkehrsstau",
      accident: "Unfall",
      construction: "Baustelle",
      road_closed: "Straße gesperrt",
      hazard: "Gefahr",
    };
    return titles[type] || "Verkehrsvorfall";
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", height: "100%", position: "relative" }}>
      {/* Sidebar */}
      <Paper sx={{ width: 400, p: 2, overflow: "auto" }}>
        <Typography variant="h5" gutterBottom>
          Verkehrsvorfälle
        </Typography>

        {config.isDevelopment && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Demo-Modus: Neue Vorfälle werden nicht gespeichert
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Button
          fullWidth
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setDialogOpen(true)}
          sx={{ mb: 2 }}
        >
          Vorfall melden
        </Button>

        {incidents.length === 0 ? (
          <Alert severity="info">Keine Verkehrsvorfälle gemeldet</Alert>
        ) : (
          <List>
            {incidents.map((incident) => (
              <ListItem key={incident.id} divider>
                <ListItemIcon>{getIncidentIcon(incident.type)}</ListItemIcon>
                <ListItemText
                  primary={
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mb: 1,
                      }}
                    >
                      <Typography variant="subtitle2">
                        {getIncidentTitle(incident.type)}
                      </Typography>
                      <Chip
                        label={incident.severity}
                        color={getSeverityColor(incident.severity)}
                        size="small"
                      />
                    </Box>
                  }
                  secondary={
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        {incident.description}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Position: {incident.lat.toFixed(4)},{" "}
                        {incident.lng.toFixed(4)}
                      </Typography>
                      {incident.created_at && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: "block" }}
                        >
                          {new Date(incident.created_at).toLocaleString(
                            "de-DE"
                          )}
                        </Typography>
                      )}
                      {config.isDevelopment && (
                        <Typography
                          variant="caption"
                          color="primary"
                          sx={{ display: "block" }}
                        >
                          Demo-Daten
                        </Typography>
                      )}
                    </Box>
                  }
                />
              </ListItem>
            ))}
          </List>
        )}
      </Paper>

      {/* Karte */}
      <Box sx={{ flex: 1 }}>
        <MapView
          incidents={incidents}
          center={[51.1657, 10.4515]}
          zoom={6}
          onMapClick={handleMapClick}
        />
      </Box>

      {/* Floating Action Button */}
      <Fab
        color="primary"
        aria-label="add incident"
        sx={{ position: "absolute", bottom: 16, right: 16 }}
        onClick={() => setDialogOpen(true)}
      >
        <AddIcon />
      </Fab>

      {/* Dialog für neuen Vorfall */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Verkehrsvorfall melden</DialogTitle>
        <DialogContent>
          {config.isDevelopment && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Demo-Modus: Vorfall wird nicht gespeichert
            </Alert>
          )}

          <FormControl fullWidth sx={{ mb: 2, mt: 1 }}>
            <InputLabel>Typ des Vorfalls</InputLabel>
            <Select
              value={newIncident.type}
              label="Typ des Vorfalls"
              onChange={(e) =>
                setNewIncident((prev) => ({ ...prev, type: e.target.value }))
              }
            >
              <MenuItem value="traffic_jam">Verkehrsstau</MenuItem>
              <MenuItem value="accident">Unfall</MenuItem>
              <MenuItem value="construction">Baustelle</MenuItem>
              <MenuItem value="road_closed">Straße gesperrt</MenuItem>
              <MenuItem value="hazard">Gefahr</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Schwere</InputLabel>
            <Select
              value={newIncident.severity}
              label="Schwere"
              onChange={(e) =>
                setNewIncident((prev) => ({
                  ...prev,
                  severity: e.target.value,
                }))
              }
            >
              <MenuItem value="low">Niedrig</MenuItem>
              <MenuItem value="medium">Mittel</MenuItem>
              <MenuItem value="high">Hoch</MenuItem>
              <MenuItem value="critical">Kritisch</MenuItem>
            </Select>
          </FormControl>

          <TextField
            fullWidth
            multiline
            rows={3}
            label="Beschreibung"
            value={newIncident.description}
            onChange={(e) =>
              setNewIncident((prev) => ({
                ...prev,
                description: e.target.value,
              }))
            }
            sx={{ mb: 2 }}
          />

          <Box sx={{ display: "flex", gap: 1 }}>
            <TextField
              label="Breitengrad"
              value={newIncident.lat}
              onChange={(e) =>
                setNewIncident((prev) => ({ ...prev, lat: e.target.value }))
              }
              type="number"
              sx={{ flex: 1 }}
            />
            <TextField
              label="Längengrad"
              value={newIncident.lng}
              onChange={(e) =>
                setNewIncident((prev) => ({ ...prev, lng: e.target.value }))
              }
              type="number"
              sx={{ flex: 1 }}
            />
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Tipp: Klicken Sie auf die Karte, um die Koordinaten automatisch zu
            setzen
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Abbrechen</Button>
          <Button
            onClick={handleSubmitIncident}
            variant="contained"
            disabled={submitting}
          >
            {submitting ? <CircularProgress size={20} /> : "Melden"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Incidents;
